import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'leagueMaker_theme';

const lightColors = {
  bg: '#ffffff',
  bgSecondary: '#f5f7fa',
  text: '#333333',
  textSecondary: '#666666',
  border: '#e0e0e0',
  card: '#ffffff',
  cardHover: '#f9f9f9',
  brand: '#667eea',
  brandGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  // Additional useful colors
  inputBg: '#ffffff',
  inputBorder: '#ddd',
  modalOverlay: 'rgba(0,0,0,0.5)',
  shadow: 'rgba(0,0,0,0.08)',
  shadowHover: 'rgba(0,0,0,0.15)',
  tableBg: '#ffffff',
  tableStripe: '#f9fafb',
  tableHeader: '#f5f7fa',
  badgeBg: 'rgba(102,126,234,0.1)',
  badgeText: '#667eea',
  linkText: '#667eea',
  navBg: '#ffffff',
  navBorder: '#e0e0e0',
  heroBg: '#f5f5f5',
  sectionBg: '#f9fafb',
  footerBg: '#1a1a2e',
  footerText: '#a0a0a0',
};

const darkColors = {
  bg: '#1a1a2e',
  bgSecondary: '#16213e',
  text: '#e0e0e0',
  textSecondary: '#a0a0a0',
  border: '#2a2a4a',
  card: '#1e1e3a',
  cardHover: '#252550',
  brand: '#667eea',
  brandGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  // Additional useful colors
  inputBg: '#252550',
  inputBorder: '#3a3a5a',
  modalOverlay: 'rgba(0,0,0,0.7)',
  shadow: 'rgba(0,0,0,0.3)',
  shadowHover: 'rgba(0,0,0,0.5)',
  tableBg: '#1e1e3a',
  tableStripe: '#232348',
  tableHeader: '#252550',
  badgeBg: 'rgba(102,126,234,0.2)',
  badgeText: '#8fa4f0',
  linkText: '#8fa4f0',
  navBg: '#16213e',
  navBorder: '#2a2a4a',
  heroBg: '#16213e',
  sectionBg: '#16213e',
  footerBg: '#0f0f1e',
  footerText: '#a0a0a0',
};

function getInitialTheme() {
  // 1. Check localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (e) {
    // localStorage not available
  }
  // 2. Check system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  // 3. Default to light
  return 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      // localStorage not available
    }
  }, [theme]);

  // Set body background, color, and data-theme attribute to prevent flash
  useEffect(() => {
    const colors = theme === 'dark' ? darkColors : lightColors;
    document.body.style.backgroundColor = colors.bg;
    document.body.style.color = colors.text;
    document.documentElement.style.backgroundColor = colors.bg;
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const colors = theme === 'dark' ? darkColors : lightColors;

  const value = useMemo(() => ({ theme, toggleTheme, colors }), [theme, colors]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
